import { useState } from "react";
import useCallbackStable from "../../../hooks/use-callback-stable";
import {
  requestNotificationPermissionIfNeeded,
  syncPushTokenIfPossible,
} from "../../../services/firebase";
import { useNotificationPermissionState } from "./useNotificationPermissionState";
import { notificationPromptDismissedAtStore } from "../../user/stores/pushTokensStore";
import { androidOpenPushSettings, isAndroidApp } from "../../../tools/android";
import { iosOpenPushSettings, isIOSApp } from "../../../tools/ios";

/**
 * What the user actually did with a push opt-in ask.
 *
 * Every surface reports this to analytics, because "the ask was shown" and "the
 * ask was accepted" are different numbers and only the pair is a rate. The
 * outcome is the answer we can attribute — not the resulting permission state,
 * which a user can flip in OS settings later without ever touching our UI.
 */
export type PushOptInOutcome = "granted" | "denied" | "later" | "settings";

/**
 * Resolve what the OS prompt actually returned.
 *
 * The token sync is deliberately NOT part of the verdict: the permission IS
 * granted whether or not the token write lands, and reporting "denied" because
 * of a failed sync would misattribute the user's answer in every funnel that
 * reads this event.
 */
async function requestPermissionOutcome(): Promise<PushOptInOutcome> {
  try {
    const granted = await requestNotificationPermissionIfNeeded();
    if (!granted) return "denied";
  } catch {
    // The prompt itself failed — no answer we can attribute to the user.
    return "denied";
  }

  try {
    await syncPushTokenIfPossible({ force: true, reason: "permissionGranted" });
  } catch {
    // Best-effort: see above. The next `syncPushTokenIfPossible` retries.
  }
  return "granted";
}

/**
 * The push opt-in behaviour shared by every notification surface (the full-page
 * `NotificationPrompt`, the post-profiling `PushOptInModal` and the JuisciGPT
 * `PushSuggestionCard`): the OS permission request + token sync, the
 * denied-on-native "open settings" fallback, and the dismissed-at bookkeeping.
 *
 * `onDone` is the caller's completion action, and receives WHAT HAPPENED so the
 * surface can report it. It is called on every exit — there is no path that
 * closes an ask without one.
 */
export function useNotificationOptIn(
  onDone: (outcome: PushOptInOutcome) => void
): {
  isRequesting: boolean;
  showOpenSettings: boolean;
  enable: () => void;
  openSettings: () => void;
  maybeLater: () => void;
} {
  const [isRequesting, setIsRequesting] = useState(false);
  const { permissionState } = useNotificationPermissionState();
  const isNative = isIOSApp() || isAndroidApp();
  const showOpenSettings =
    permissionState !== null && permissionState.status === "denied" && isNative;

  const requestPermission = useCallbackStable(async () => {
    const outcome = await requestPermissionOutcome();
    notificationPromptDismissedAtStore.set(Date.now());
    onDone(outcome);
  });

  function enable(): void {
    // Notification.requestPermission MUST fire synchronously from the click
    // handler: browsers that gate the prompt on transient user activation
    // (Safari) reject a deferred request, leaving the permission "default"
    // forever. The promise-based API is non-blocking — the browser's native
    // prompt is its own UI — so the spinner paints while it is awaited.
    setIsRequesting(true);
    void requestPermission();
  }

  function openSettings(): void {
    if (isIOSApp()) iosOpenPushSettings();
    else if (isAndroidApp()) androidOpenPushSettings();
    notificationPromptDismissedAtStore.set(Date.now());
    onDone("settings");
  }

  function maybeLater(): void {
    notificationPromptDismissedAtStore.set(Date.now());
    onDone("later");
  }

  return { isRequesting, showOpenSettings, enable, openSettings, maybeLater };
}
